// client/src/coms/organisation/orgShared.js
// Small shared pieces for the organisation views: formatting, provider badges, toasts, confirmation dialogs,
// empty/loading states and the "open this item" navigation event.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './org.css';

export function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return '—';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(v < 10240 ? 1 : 0)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / 1024 / 1024).toFixed(v < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  return `${(v / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

// Server timestamps are UTC "YYYY-MM-DD HH:MM:SS" or ISO strings.
export function parseTime(s) {
  if (!s) return null;
  const str = String(s);
  const iso = /\dT\d/.test(str) ? str : `${str.replace(' ', 'T')}Z`;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t) : null;
}

export function formatDate(s, { time = true } = {}) {
  const d = parseTime(s);
  if (!d) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay && time) return `Today ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  return d.toLocaleDateString([], { year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric', month: 'short', day: 'numeric' }) + (time ? ` ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '');
}

export const PROVIDERS = {
  voxemera: { label: 'Voxemera', color: '#5865f2', glyph: 'V' },
  gdrive: { label: 'Google Drive', color: '#1fa463', glyph: '▲' },
  dropbox: { label: 'Dropbox', color: '#0061ff', glyph: '◆' },
  onedrive: { label: 'OneDrive', color: '#0078d4', glyph: '☁' },
  github: { label: 'GitHub', color: '#6e7681', glyph: '⌥' },
  gitlab: { label: 'GitLab', color: '#e24329', glyph: '⬢' },
  s3: { label: 'S3 storage', color: '#c7511f', glyph: '▤' },
};

export function ProviderBadge({ provider, withLabel = false }) {
  const p = PROVIDERS[provider] || PROVIDERS.voxemera;
  return (
    <span className="vxo-provider" title={`Stored in ${p.label}`}>
      <span className="vxo-provider-dot" aria-hidden="true" style={{ background: p.color }}>
        {p.glyph}
      </span>
      {withLabel ? <span className="vxo-provider-label">{p.label}</span> : <span className="vxo-sr">{p.label}</span>}
    </span>
  );
}

export function kindIcon(mime, { folder = false, kind = '' } = {}) {
  if (folder) return '📁';
  const m = String(mime || kind || '');
  if (m.startsWith('image') || kind === 'image') return '🖼️';
  if (m.startsWith('video') || kind === 'video') return '🎞️';
  if (m.startsWith('audio') || kind === 'audio') return '🎵';
  if (m === 'application/pdf') return '📕';
  if (m.startsWith('text') || m === 'application/json') return '📄';
  if (/zip|rar|7z|gzip/.test(m) || kind === 'file') return '🗜️';
  if (/document|msword|sheet|presentation|opendocument/.test(m)) return '📝';
  return '📎';
}

// ---- toasts ----
const listeners = new Set();
let toastSeq = 0;
export function notify(text, opts = {}) {
  const t = { id: ++toastSeq, text: String(text || ''), tone: opts.tone || 'info', action: opts.action || null, ms: opts.ms || 4200 };
  for (const fn of listeners) fn(t);
}

export function ToastHost() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const fn = (t) => {
      setItems((prev) => [...prev.slice(-3), t]);
      setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== t.id)), t.ms);
    };
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="vxo-toasts vx-pz" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`vxo-toast is-${t.tone}`}>
          <span>{t.text}</span>
          {t.action ? (
            <button
              type="button"
              className="vx-btn small"
              onClick={() => {
                t.action.onClick();
                setItems((prev) => prev.filter((x) => x.id !== t.id));
              }}
            >
              {t.action.label}
            </button>
          ) : null}
        </div>
      ))}
    </div>,
    document.body
  );
}

// ---- dialogs ----
// Open dialogs, innermost last: only the top one reacts to Escape/Tab, so nested dialogs behave.
const dialogStack = [];

export function Dialog({ title, children, onClose, footer, wide = false, labelledBy }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const token = {};
    dialogStack.push(token);
    const prev = document.activeElement;
    const t = setTimeout(() => {
      const el = ref.current && ref.current.querySelector('input, textarea, select, button:not([data-dialog-close])');
      if (el) el.focus();
    }, 0);
    const onKey = (e) => {
      if (dialogStack[dialogStack.length - 1] !== token) return;
      // An external picker (Google Drive) is open above this dialog: keys belong to it, not to this dialog.
      if (document.body.classList.contains('vx-external-picker')) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (closeRef.current) closeRef.current();
      }
      if (e.key === 'Tab' && ref.current) {
        const els = Array.from(ref.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter((x) => !x.disabled);
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey, true);
      const i = dialogStack.indexOf(token);
      if (i >= 0) dialogStack.splice(i, 1);
      if (prev && prev.focus) prev.focus();
    };
  }, []);
  return createPortal(
    <div className="vxo-backdrop vx-pz" onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div ref={ref} className={`vxo-dialog${wide ? ' is-wide' : ''}`} role="dialog" aria-modal="true" aria-label={labelledBy ? undefined : title} aria-labelledby={labelledBy}>
        <div className="vxo-dialog-head">
          <h2 className="vxo-dialog-title">{title}</h2>
          <button type="button" className="vx-icon-btn" aria-label="Close" data-dialog-close onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="vxo-dialog-body">{children}</div>
        {footer ? <div className="vxo-dialog-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

// confirm({ title, body, confirmLabel, danger }) -> Promise<boolean>
export function useConfirm() {
  const [state, setState] = useState(null);
  const confirm = (opts) => new Promise((resolve) => setState({ ...opts, resolve }));
  const element = state ? (
    <Dialog
      title={state.title || 'Are you sure?'}
      onClose={() => {
        state.resolve(false);
        setState(null);
      }}
      footer={
        <>
          <button
            type="button"
            className="vx-btn"
            onClick={() => {
              state.resolve(false);
              setState(null);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`vx-btn primary${state.danger ? ' danger' : ''}`}
            onClick={() => {
              state.resolve(true);
              setState(null);
            }}
          >
            {state.confirmLabel || 'Confirm'}
          </button>
        </>
      }
    >
      <div className="vxo-confirm-body">{state.body}</div>
    </Dialog>
  ) : null;
  return [confirm, element];
}

// prompt({ title, label, value, placeholder, multiline }) -> Promise<string|null>
export function usePrompt() {
  const [state, setState] = useState(null);
  const prompt = (opts) => new Promise((resolve) => setState({ ...opts, value: opts.value || '', resolve }));
  const close = (v) => {
    state.resolve(v);
    setState(null);
  };
  const element = state ? (
    <Dialog
      title={state.title}
      onClose={() => close(null)}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={() => close(null)}>
            Cancel
          </button>
          <button type="button" className="vx-btn primary" onClick={() => close(state.value)}>
            {state.confirmLabel || 'Save'}
          </button>
        </>
      }
    >
      <label className="vxo-field">
        <span>{state.label}</span>
        {state.multiline ? (
          <textarea value={state.value} placeholder={state.placeholder} rows={4} maxLength={state.maxLength || 1000} onChange={(e) => setState({ ...state, value: e.target.value })} />
        ) : (
          <input
            value={state.value}
            placeholder={state.placeholder}
            maxLength={state.maxLength || 200}
            onChange={(e) => setState({ ...state, value: e.target.value })}
            onKeyDown={(e) => {
              // preventDefault: focus returns to the button that opened the prompt, and the same Enter
              // would otherwise press it again and reopen the prompt.
              if (e.key === 'Enter') {
                e.preventDefault();
                close(state.value);
              }
            }}
          />
        )}
      </label>
      {state.hint ? <p className="vxo-hint">{state.hint}</p> : null}
    </Dialog>
  ) : null;
  return [prompt, element];
}

export function Spinner({ label = 'Loading…' }) {
  return (
    <div className="vxo-loading" role="status">
      <span className="vxo-spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({ icon = '🗂️', title, children }) {
  return (
    <div className="vxo-empty">
      <div className="vxo-empty-icon" aria-hidden="true">
        {icon}
      </div>
      <div className="vxo-empty-title">{title}</div>
      {children ? <div className="vxo-empty-body">{children}</div> : null}
    </div>
  );
}

export function ErrorNote({ error, onRetry }) {
  if (!error) return null;
  return (
    <div className="vxo-error" role="alert">
      <span>{String(error.message || error)}</span>
      {onRetry ? (
        <button type="button" className="vx-btn small" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

// Navigation: views ask the shell to open something (a message, channel, file, resource, DM, canvas, link).
export function openTarget(open) {
  if (!open) return;
  if (open.kind === 'link' && open.url) {
    window.open(open.url, '_blank', 'noopener,noreferrer');
    return;
  }
  try {
    window.dispatchEvent(new CustomEvent('vox-open-target', { detail: open }));
  } catch {}
}

// Opens one of the organisation areas ('library' | 'search' | 'bookmarks') with optional context.
export function openArea(area, detail = {}) {
  try {
    window.__voxPendingOrg = { area, ...detail };
    window.dispatchEvent(new CustomEvent('vox-open-org', { detail: { area, ...detail } }));
  } catch {}
}
